import { createFileRoute } from "@tanstack/react-router";

import { LanguagePicker, LegalDocument } from "../components/LegalDocument";
import { PageChrome } from "../components/PageChrome";
import {
  legalPath,
  loadLegalBundle,
  resolveLegalLang,
} from "../lib/legalDocs/registry";
import { alternates, canonical, documentHeaders } from "../lib/seo";

/**
 * The Privacy Policy, in whichever of the nine app languages `?lang=` asks for.
 *
 * `/privacy` is English and the canonical URL — it is what the App Store listing
 * and the in-app rows point at — and the other eight ride in a query parameter
 * with an English fallback, so a stale or malformed link still renders a policy
 * instead of a 404.
 *
 * ── The canonical follows the language ───────────────────────────────────────
 *
 * It used to be `/privacy` on all nine, which is a cross-locale canonical: it
 * asks Google to drop the eight translations, and it disqualified the whole
 * hreflang cluster at the same time (a set that omits its own canonical is
 * ignored). Both now come from `legalPath`, so a Spanish reader's URL,
 * canonical and `hreflang="es"` are the same string. See the note there.
 *
 * The chosen language is loaded in the route loader rather than imported: nine
 * translations statically imported end up in the client entry chunk, which
 * would make every visitor to the home page pay for a document they never open.
 */
export const Route = createFileRoute("/privacy")({
  validateSearch: (search: Record<string, unknown>): { lang?: string } => ({
    lang: typeof search.lang === "string" ? search.lang : undefined,
  }),
  loaderDeps: ({ search }) => ({ lang: resolveLegalLang(search.lang) }),
  loader: ({ deps }) => loadLegalBundle(deps.lang),
  headers: documentHeaders,
  head: ({ match }) => ({
    meta: [{ title: "Privacy Policy — Fazly" }],
    links: [
      {
        rel: "canonical",
        href: canonical(legalPath("/privacy", match.loaderDeps.lang)),
      },
      ...alternates((code) => legalPath("/privacy", code)),
    ],
  }),
  component: Privacy,
});

function Privacy() {
  const { lang } = Route.useLoaderDeps();
  const bundle = Route.useLoaderData();

  return (
    <PageChrome title={bundle.privacy.title}>
      <LanguagePicker
        path="/privacy"
        current={lang}
        label={bundle.ui.languageLabel}
      />
      <LegalDocument doc={bundle.privacy} bundle={bundle} lang={lang} />
    </PageChrome>
  );
}
